/**
 * Filer Model
 * Represents a SEC 13F filer with name resolution from EDGAR API.
 *
 * Usage:
 *   const filer = await Filer.find('0001067983');
 *   console.log(filer.name); // "BERKSHIRE HATHAWAY INC"
 *
 * Precondition: CIK must be a valid SEC identifier
 * Postcondition: Returns Filer instance with resolved name
 */

import { fetchSubmissions } from '../lib/financeapis.js';
import { parseCompanyData, type CompanyData } from '../lib/parsers.js';

export class Filer {
  readonly cik: string;
  readonly name: string;
  readonly tickers: string[];
  readonly entityType?: string;

  private constructor(data: CompanyData) {
    this.cik = data.cik;
    this.name = data.name;
    this.tickers = data.tickers || [];
    this.entityType = data.entityType;
  }

  /**
   * Find a filer by CIK.
   * Fetches from SEC EDGAR API (deduped via clientFetch).
   */
  static async find(cik: string): Promise<Filer | null> {
    const result = await fetchSubmissions(cik);
    if (!result.ok) return null;

    const data = parseCompanyData(result.data);
    if (!data.name) return null;

    return new Filer(data);
  }

  /**
   * Build a Filer from known attributes (no API call).
   * Use when name is already available.
   */
  static build(cik: string, name: string, tickers: string[] = []): Filer {
    return new Filer({ cik, name, tickers });
  }

  /**
   * Find or build a filer.
   * Returns existing data if name provided, fetches if not.
   */
  static async findOrBuild(cik: string, name?: string): Promise<Filer | null> {
    if (name) {
      return Filer.build(cik, name);
    }
    return Filer.find(cik);
  }

  toJSON(): { cik: string; name: string; tickers: string[] } {
    return {
      cik: this.cik,
      name: this.name,
      tickers: this.tickers,
    };
  }
}
